import React, { useState, useEffect, useCallback } from 'react';
import { userAPI, departmentAPI, roleAPI } from '../../services/api';
import { Button, Input, Alert, Modal, Loading } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import * as permissionUtils from '../../utils/permissions';
import './AdminPages.css';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalError, setModalError] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password_confirm: '',
    real_name: '',
    phone: '',
    employee_id: '',
    department: '',
    position: '',
    role_ids: [],
    is_active: true,
  });

  const [newPassword, setNewPassword] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, deptsRes, rolesRes] = await Promise.all([
        userAPI.list({ search: searchTerm, page, page_size: pageSize }),
        departmentAPI.list(),
        roleAPI.list(),
      ]);
      const listData = usersRes.data.results || usersRes.data;
      setUsers(listData);
      setTotal(usersRes.data.count ?? listData.length ?? 0);
      setDepartments(deptsRes.data.results || deptsRes.data);
      setRoles(rolesRes.data.results || rolesRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: '获取数据失败' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    setPage(1);
    fetchData();
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    setPage(Math.min(newPage, maxPage));
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      password_confirm: '',
      real_name: '',
      phone: '',
      employee_id: '',
      department: '',
      position: '',
      role_ids: [],
      is_active: true,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      // 处理空字符串字段，转换为 null
      const submitData = {
        ...formData,
        department: formData.department || null,
        phone: formData.phone || null,
        employee_id: formData.employee_id || null,
        email: formData.email || null,
      };
      await userAPI.create(submitData);
      setMessage({ type: 'success', text: '用户创建成功' });
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      const errors = err.response?.data;
      const errorMsg = errors?.username?.[0] || errors?.email?.[0] || errors?.password?.[0] || errors?.detail || '创建失败';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setModalError('');

    try {
      const { password, password_confirm, username, ...rest } = formData;
      // 处理空字符串字段，转换为 null
      const updateData = {
        ...rest,
        department: rest.department || null,
        phone: rest.phone || null,
        employee_id: rest.employee_id || null,
        email: rest.email || null,
      };
      await userAPI.update(selectedUser.id, updateData);
      setMessage({ type: 'success', text: '用户更新成功' });
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '更新失败';
      setModalError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setModalError('');
    setFormData({
      ...formData,
      username: user.username,
      email: user.email || '',
      real_name: user.real_name || '',
      phone: user.phone || '',
      employee_id: user.employee_id || '',
      department: user.department || '',
      position: user.position || '',
      role_ids: user.roles_info?.map(r => r.id) || [],
      is_active: user.is_active,
    });
    setShowEditModal(true);
  };

  const handleToggleLock = async (user) => {
    try {
      if (user.is_locked) {
        await userAPI.unlock(user.id);
        setMessage({ type: 'success', text: '用户已解锁' });
      } else {
        await userAPI.lock(user.id, '管理员锁定');
        setMessage({ type: 'success', text: '用户已锁定' });
      }
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '操作失败' });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      setMessage({ type: 'error', text: '密码长度至少8位' });
      return;
    }

    setFormLoading(true);
    try {
      await userAPI.resetPassword(selectedUser.id, newPassword);
      setMessage({ type: 'success', text: '密码重置成功' });
      setShowResetPasswordModal(false);
      setNewPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: '密码重置失败' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`确定要删除用户 "${user.real_name || user.username}" 吗？`)) {
      return;
    }

    try {
      await userAPI.delete(user.id);
      setMessage({ type: 'success', text: '用户已删除' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '删除失败' });
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return <Loading fullScreen text="加载中..." />;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>用户管理</h1>
          <p>管理系统用户账号</p>
        </div>
        {permissionUtils.canCreateUser(currentUser) && (
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            添加用户
          </Button>
        )}
      </div>

      {message.text && (
        <Alert
          type={message.type}
          message={message.text}
          onClose={() => setMessage({ type: '', text: '' })}
        />
      )}

      <div className="admin-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <Input
            placeholder="搜索用户名、姓名、邮箱、工号..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth={false}
          />
          <Button type="submit" variant="secondary">搜索</Button>
        </form>
      </div>

      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr>
              <th>用户名</th>
              <th>姓名</th>
              <th>工号</th>
              <th>部门</th>
              <th>角色</th>
              <th>邮箱</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user) => (
                <tr
                  key={user.id}
                  onClick={() => { setSelectedUser(user); setShowDetailModal(true); }}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar">
                        {(user.real_name || user.username)[0].toUpperCase()}
                      </div>
                      <span>{user.username}</span>
                    </div>
                  </td>
                  <td>{user.real_name || '-'}</td>
                  <td>{user.employee_id || '-'}</td>
                  <td>{user.department_name || '-'}</td>
                  <td>{user.roles_info?.map((r) => r.name).join(', ') || '-'}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`status-badge ${user.is_locked ? 'locked' : user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_locked ? '已锁定' : user.is_active ? '正常' : '已禁用'}
                    </span>
                  </td>
                  <td>
                    <div
                      className="action-buttons"
                      onClick={(e) => e.stopPropagation()} // 防止点击操作按钮触发详情
                    >
                      {permissionUtils.canEditUser(currentUser) && (
                        <button
                          className="action-btn"
                          onClick={(e) => { e.stopPropagation(); openEditModal(user); }}
                          title="编辑"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      )}
                      {permissionUtils.canLockUser(currentUser) && (
                        <button
                          className="action-btn"
                          onClick={(e) => { e.stopPropagation(); handleToggleLock(user); }}
                          title={user.is_locked ? '解锁' : '锁定'}
                        >
                          {user.is_locked ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          )}
                        </button>
                      )}
                      {permissionUtils.canResetPassword(currentUser) && (
                        <button 
                          className="action-btn" 
                          onClick={(e) => { e.stopPropagation(); setSelectedUser(user); setShowResetPasswordModal(true); }}
                          title="重置密码"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                          </svg>
                        </button>
                      )}
                      {permissionUtils.canDeleteUser(currentUser) && (
                        <button
                          className="action-btn danger"
                          onClick={(e) => { e.stopPropagation(); handleDelete(user); }}
                          title="删除"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="empty-cell">暂无用户数据</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          marginTop: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: 'var(--gray-600)', fontSize: '0.95rem' }}>
          共 {total} 条
        </span>
        <label style={{ color: 'var(--gray-600)', fontSize: '0.95rem' }}>
          每页
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: '0.35rem' }}
          >
            {[10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          variant="secondary"
          onClick={() => handlePageChange(page - 1)}
          disabled={page <= 1}
        >
          上一页
        </Button>
        <span style={{ color: 'var(--gray-700)' }}>
          第 {page} / {Math.max(1, Math.ceil(total / pageSize) || 1)} 页
        </span>
        <Button
          type="button"
          variant="secondary"
          onClick={() => handlePageChange(page + 1)}
          disabled={page >= Math.max(1, Math.ceil(total / pageSize))}
        >
          下一页
        </Button>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="添加用户"
        size="large"
      >
        <form onSubmit={handleCreate} className="modal-form">
          <div className="form-grid">
            <Input
              label="用户名 *"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
            <Input
              label="邮箱 *"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            <Input
              label="密码 *"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <Input
              label="确认密码 *"
              type="password"
              value={formData.password_confirm}
              onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
              required
            />
            <Input
              label="真实姓名"
              value={formData.real_name}
              onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
            />
            <Input
              label="工号"
              value={formData.employee_id}
              onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
            />
            <Input
              label="手机号"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <Input
              label="职位"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">部门</label>
            <select
              className="form-select"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            >
              <option value="">请选择部门</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">角色</label>
            <div className="checkbox-group">
              {roles.map((role) => (
                <label key={role.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    disabled={!permissionUtils.canAssignRoles(currentUser)}
                    checked={formData.role_ids.includes(role.id)}
                    onChange={(e) => {
                      if (!permissionUtils.canAssignRoles(currentUser)) return;
                      const newRoles = e.target.checked
                        ? [...formData.role_ids, role.id]
                        : formData.role_ids.filter(id => id !== role.id);
                      setFormData({ ...formData, role_ids: newRoles });
                    }}
                  />
                  <span>{role.name}</span>
                </label>
              ))}
            </div>
            {!permissionUtils.canAssignRoles(currentUser) && (
              <p style={{ fontSize: '0.875rem', color: '#d97706', marginTop: '0.5rem' }}>
                仅管理员可以分配角色
              </p>
            )}
          </div>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowCreateModal(false)}>
              取消
            </Button>
            <Button type="submit" loading={formLoading}>
              创建
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="编辑用户"
        size="large"
      >
        {modalError && <Alert type="error" message={modalError} />}
        <form onSubmit={handleEdit} className="modal-form">
          <div className="form-grid">
            <Input
              label="用户名"
              value={formData.username}
              disabled
            />
            <Input
              label="邮箱"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <Input
              label="真实姓名"
              value={formData.real_name}
              onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
            />
            <Input
              label="工号"
              value={formData.employee_id}
              onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
            />
            <Input
              label="手机号"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <Input
              label="职位"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">部门</label>
            <select
              className="form-select"
              disabled={!permissionUtils.canEditDepartment(currentUser)}
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            >
              <option value="">请选择部门</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
            {!permissionUtils.canEditDepartment(currentUser) && (
              <p style={{ fontSize: '0.875rem', color: '#d97706', marginTop: '0.5rem' }}>
                仅管理员可以修改部门
              </p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">角色</label>
            <div className="checkbox-group">
              {roles.map((role) => (
                <label key={role.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    disabled={!permissionUtils.canAssignRoles(currentUser)}
                    checked={formData.role_ids.includes(role.id)}
                    onChange={(e) => {
                      if (!permissionUtils.canAssignRoles(currentUser)) return;
                      const newRoles = e.target.checked
                        ? [...formData.role_ids, role.id]
                        : formData.role_ids.filter(id => id !== role.id);
                      setFormData({ ...formData, role_ids: newRoles });
                    }}
                  />
                  <span>{role.name}</span>
                </label>
              ))}
            </div>
            {!permissionUtils.canAssignRoles(currentUser) && (
              <p style={{ fontSize: '0.875rem', color: '#d97706', marginTop: '0.5rem' }}>
                仅管理员可以分配角色
              </p>
            )}
          </div>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowEditModal(false)}>
              取消
            </Button>
            <Button type="submit" loading={formLoading}>
              保存
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetPasswordModal}
        onClose={() => { setShowResetPasswordModal(false); setNewPassword(''); }}
        title="重置密码"
        size="small"
      >
        <form onSubmit={handleResetPassword} className="modal-form">
          <p style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
            为用户 <strong>{selectedUser?.real_name || selectedUser?.username}</strong> 设置新密码
          </p>
          <Input
            label="新密码"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="请输入新密码（至少8位）"
            required
          />
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
              取消
            </Button>
            <Button type="submit" loading={formLoading}>
              确认重置
            </Button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="员工详情"
        size="large"
      >
        {selectedUser && (
          <div className="detail-content">
            <div className="detail-section">
              <h3 className="detail-title">基本信息</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>用户名</label>
                  <p>{selectedUser.username}</p>
                </div>
                <div className="detail-item">
                  <label>真实姓名</label>
                  <p>{selectedUser.real_name || '-'}</p>
                </div>
                <div className="detail-item">
                  <label>工号</label>
                  <p>{selectedUser.employee_id || '-'}</p>
                </div>
                <div className="detail-item">
                  <label>邮箱</label>
                  <p>{selectedUser.email || '-'}</p>
                </div>
                <div className="detail-item">
                  <label>手机号</label>
                  <p>{selectedUser.phone || '-'}</p>
                </div>
                <div className="detail-item">
                  <label>职位</label>
                  <p>{selectedUser.position || '-'}</p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3 className="detail-title">组织信息</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>部门</label>
                  <p>{selectedUser.department_name || '-'}</p>
                </div>
                <div className="detail-item">
                  <label>角色</label>
                  <p>{selectedUser.roles_info?.map((r) => r.name).join(', ') || '-'}</p>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h3 className="detail-title">账号状态</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>账号状态</label>
                  <p>
                    <span className={`status-badge ${selectedUser.is_locked ? 'locked' : selectedUser.is_active ? 'active' : 'inactive'}`}>
                      {selectedUser.is_locked ? '已锁定' : selectedUser.is_active ? '正常' : '已禁用'}
                    </span>
                  </p>
                </div>
                {selectedUser.is_locked && (
                  <div className="detail-item">
                    <label>锁定原因</label>
                    <p>{selectedUser.lock_reason || '-'}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3 className="detail-title">时间信息</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>注册时间</label>
                  <p>{formatDate(selectedUser.date_joined)}</p>
                </div>
                <div className="detail-item">
                  <label>最后登录</label>
                  <p>{formatDate(selectedUser.last_login) || '-'}</p>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <Button type="button" variant="secondary" onClick={() => setShowDetailModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UsersPage;

